import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { BreakawayHullState, BreakawaySection, CombinedPerformance } from '../../types/breakawayHull';
import { Drive } from '../../types/drive';
import { PowerPlant } from '../../types/powerPlants';
import { Bridge } from '../../types/bridge';
import { Sensors } from '../../types/sensors';
import { Armor } from '../../types/armor';
import { FuelRequirements } from '../../types/fuel';
import { Computer } from '../../types/computer';

const initialState: BreakawayHullState = {
  isBreakaway: false,
  sections: [],
  combinedPerformance: {
    maneuver: 0,
    jump: 0,
    reaction: 0,
    totalPower: 0,
    totalFuel: 0,
    totalCost: 0,
    totalArmorWeight: 0,
    totalArmorCost: 0,
  },
  totalCost: 0,
  totalTonnage: 0,
  separationTime: "1D rounds",
};

const breakawayHullSlice = createSlice({
  name: 'breakawayHull',
  initialState,
  reducers: {
    // Initialize breakaway hull with sections
    initializeBreakawayHull: (state, action: PayloadAction<{ sections: Array<{ id: string; name: string; size: number }> }>) => {
      state.isBreakaway = true;
      state.sections = action.payload.sections.map(section => ({
        id: section.id,
        name: section.name,
        size: section.size,
        hullPoints: Math.floor(section.size * 2.5), // Basic hull points calculation
        bridge: null,
        powerPlant: null,
        computer: null,
        backupComputer: null,
        drives: {
          maneuver: null,
          jump: null,
          reaction: null,
        },
        sensors: {
          type: "BREAKAWAY",
          name: "Breakaway Hull Sensors",
          description: "Specialized sensor suite for breakaway hull sections",
          capabilities: ["Lidar", "Radar"],
          dm: -1,
          power: 7,
          tons: 1,
          cost: 1500000,
          techLevel: 9,
          rating: 1,
        },
        weapons: [],
        armor: null,
        fuelAllocation: {
          percentage: 0,
          tons: 0,
          requirements: {
            reactionDrive: 0,
            jumpDrive: 0,
            powerPlant: 0,
            total: 0,
          },
        },
      }));
      state.totalTonnage = action.payload.sections.reduce((sum, section) => sum + section.size, 0);
    },

    // Set section drive
    setSectionDrive: (state, action: PayloadAction<{ sectionId: string; driveType: 'maneuver' | 'jump' | 'reaction'; drive: Drive | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.drives[action.payload.driveType] = action.payload.drive;
        // Don't recalculate combined performance - it should be set by user input
      }
    },

    // Set section power plant
    setSectionPowerPlant: (state, action: PayloadAction<{ sectionId: string; powerPlant: PowerPlant | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.powerPlant = action.payload.powerPlant;
        // Don't recalculate combined performance - it should be set by user input
      }
    },

    // Set section bridge
    setSectionBridge: (state, action: PayloadAction<{ sectionId: string; bridge: Bridge | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.bridge = action.payload.bridge;
      }
    },

    // Set section sensors
    setSectionSensors: (state, action: PayloadAction<{ sectionId: string; sensors: Sensors | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.sensors = action.payload.sensors;
      }
    },

    // Set section computer
    setSectionComputer: (state, action: PayloadAction<{ sectionId: string; computer: Computer | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.computer = action.payload.computer;
      }
    },

    // Set section backup computer
    setSectionBackupComputer: (state, action: PayloadAction<{ sectionId: string; backupComputer: Computer | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.backupComputer = action.payload.backupComputer;
      }
    },

    // Update entire breakaway section
    updateBreakawaySection: (state, action: PayloadAction<{ sectionId: string; section: BreakawaySection }>) => {
      const index = state.sections.findIndex(s => s.id === action.payload.sectionId);
      if (index !== -1) {
        state.sections[index] = action.payload.section;
        // Don't recalculate combined performance - it should be set by user input
      }
    },

    // Set active section
    setActiveSection: (state, action: PayloadAction<string>) => {
      state.activeSectionId = action.payload;
    },

    // Set section armor
    setSectionArmor: (state, action: PayloadAction<{ sectionId: string; armor: Armor | null }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.armor = action.payload.armor;
      }
    },

    // Set section fuel allocation
    setSectionFuelAllocation: (state, action: PayloadAction<{ sectionId: string; percentage: number; tons: number; requirements: FuelRequirements }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.fuelAllocation = {
          percentage: action.payload.percentage,
          tons: action.payload.tons,
          requirements: action.payload.requirements,
        };
      }
    },

    // Update combined performance
    updateCombinedPerformance: (state, action: PayloadAction<CombinedPerformance>) => {
      state.combinedPerformance = action.payload;
    },

    // Clear old drives (to be called from shipBuilderSlice)
    clearOldDrives: (state) => {
      // This is a marker action - the actual clearing happens in shipBuilderSlice
      console.log('Clear old drives action dispatched');
    },

    // Reset breakaway hull
    resetBreakawayHull: (state) => {
      return initialState;
    },

    // Set section stateroom configuration
    setSectionStateroomConfig: (state, action: PayloadAction<{ sectionId: string; stateroomConfig: { singleStaterooms: number; doubleStaterooms: number; lowBerths: number; emergencyLowBerths: number; commonArea: number } }>) => {
      const section = state.sections.find(s => s.id === action.payload.sectionId);
      if (section) {
        section.stateroomConfig = action.payload.stateroomConfig;
      }
    },

    // Load breakaway hull state
    loadBreakawayHull: (state, action: PayloadAction<BreakawayHullState>) => {
      Object.assign(state, action.payload);
    },
  },
});

export const {
  initializeBreakawayHull,
  setSectionDrive,
  setSectionPowerPlant,
  setSectionBridge,
  setSectionSensors,
  setSectionComputer,
  setSectionBackupComputer,
  updateBreakawaySection,
  setActiveSection,
  setSectionArmor,
  setSectionFuelAllocation,
  setSectionStateroomConfig,
  updateCombinedPerformance,
  resetBreakawayHull,
  loadBreakawayHull,
} = breakawayHullSlice.actions;

export default breakawayHullSlice.reducer; 